import type { PolicyDocument } from '../legal/types';

export const aboutDocument: PolicyDocument = {
  title: 'About GoGrabJob',
  eyebrow: 'Company · About us',
  effectiveDate: 'Founded 2025',
  lastUpdated: 'June 2026',
  summary:
    'GoGrabJob is an AI job search platform built in India for job seekers everywhere. Discover and rank roles matched to your profile, tailor your existing resume for each job, track applications, and practice role-specific interviews — in one workspace, without selling your data to recruiters.',
  sections: [
    {
      title: '1. Who we are',
      paragraphs: [
        'GoGrabJob is operated by Zykes Technologies LLP (LLPIN: ACP-5076), based in Bengaluru, India. We build software that helps candidates discover relevant roles, tailor their existing resume to real job descriptions, score ATS fit, draft cover letters, and stay organised through an active search.',
        'We are an independent product — not affiliated with GrabJobs, Naukri, LinkedIn, or any job board. We integrate with public job sources to help you discover roles; you decide what to apply to and what to share.',
      ],
    },
    {
      title: '2. Our story',
      paragraphs: [
        'GoGrabJob started in 2025 from a frustration many of us know: qualified people applying to dozens of roles and hearing nothing back. Often the issue was not talent — it was applying to the wrong roles, using the same generic resume, and losing context between tabs, chats, and spreadsheets.',
        'Job boards show listings; chat tools rewrite a resume once and forget. Applicant Tracking Systems filter candidates before a recruiter opens a file. We built GoGrabJob to close that gap: one workspace from match → tailor → apply → track → prep.',
        'Today thousands of job seekers use GoGrabJob to find better-fit roles, improve ATS scores on resumes they already have, and move faster through active searches — especially in India, where we price in INR and design for local job boards and hiring norms.',
      ],
    },
    {
      title: '3. Mission and vision',
      subsections: [
        {
          title: '3.1 Mission',
          paragraphs: [
            'Give every job seeker access to the same quality of job-search intelligence that was once limited to expensive coaches and privileged networks — starting free, upgrading only when your search intensifies.',
          ],
        },
        {
          title: '3.2 Vision',
          paragraphs: [
            'A single AI-native job search workspace where discovery, resume tailoring, application tracking, and interview prep share one context — so you spend time interviewing, not rebuilding spreadsheets and re-explaining your background in new chats.',
          ],
        },
      ],
    },
    {
      title: '4. What we build',
      list: [
        'Job discovery and ranking from major boards (Naukri, LinkedIn, Indeed, Peerlist, and more) — matched to your profile.',
        'Resume tailoring for each job description you paste or import — you upload your resume; we align it to the role.',
        'ATS compatibility scoring with clear keyword and skills feedback.',
        'Cover letter and interview-prep assistance tied to the same role context.',
        'Application tracker to manage status, notes, and follow-ups.',
        'Free tier to try the product; paid plans for active job hunts with more optimizations.',
      ],
      callout:
        'GoGrabJob is a tool for candidates. We do not generate résumés from scratch or guarantee interviews — we help you find better-fit roles and present your existing experience clearly for each application.',
    },
    {
      title: '5. By the numbers',
      stats: [
        { value: '10,000+', label: 'Resumes tailored' },
        { value: '95%', label: 'User satisfaction (support surveys)' },
        { value: '50+', label: 'Countries with active users' },
        { value: '85%', label: 'Typical ATS score improvement' },
      ],
      paragraphs: [
        'Metrics are updated periodically and reflect aggregate platform usage. Individual results vary by role, industry, and application quality.',
      ],
    },
    {
      title: '6. Our values',
      cards: [
        {
          title: 'Mission-driven',
          body: 'Everyone deserves a fair shot at roles they are qualified for — regardless of background, college tier, or access to career coaches.',
        },
        {
          title: 'Innovation with purpose',
          body: 'We use modern AI where it helps: ranking roles, parsing job descriptions, and suggesting resume edits — never to misrepresent your experience.',
        },
        {
          title: 'User-first product',
          body: 'Features ship from real job-search pain points. We measure success by whether you land conversations, not by vanity metrics.',
        },
        {
          title: 'Privacy by design',
          body: 'Your resume stays yours. We do not sell personal data to recruiters. Export and delete your account from Settings at any time.',
        },
      ],
    },
    {
      title: '7. Technology and AI',
      paragraphs: [
        'GoGrabJob combines large language models with structured parsing of resumes and job descriptions. The system extracts skills, compares them to role requirements, and suggests edits that improve machine-readable fit while keeping your voice.',
      ],
      subsections: [
        {
          title: '7.1 How we use AI responsibly',
          list: [
            'Outputs are drafts for your review — you approve before applying.',
            'Processing is scoped to your session and service delivery.',
            'We do not train public third-party models on your resume without consent.',
          ],
        },
        {
          title: '7.2 Infrastructure',
          list: [
            'Cloud-hosted on modern providers with encryption in transit and at rest.',
            'Authentication and data storage via Supabase; payments via Cashfree / Razorpay.',
            'AI inference via established model providers under strict data-processing terms.',
          ],
        },
      ],
    },
    {
      title: '8. Trust and compliance',
      paragraphs: [
        'We publish clear Privacy, Cookie, GDPR, and DPDP policies. Our Trust Center describes security practices and subprocessors. We align with applicable data-protection law in India, the EEA, and the UK.',
      ],
      list: [
        'Privacy Policy — how we collect, use, and delete data.',
        'Trust Center — security controls and partners.',
        'DPDP Compliance — India Digital Personal Data Protection Act.',
        'GDPR Compliance — rights for EEA / UK users.',
      ],
    },
    {
      title: '9. Meet the founders',
      team: [
        {
          name: 'Dharmendra Singh',
          role: 'Founder',
          bio: 'Building GoGrabJob so every job seeker can discover the right roles and tailor their story for each one — not just the candidates who can afford a career coach.',
          image: '/team/dharmendra-singh.png',
          linkedIn: 'https://www.linkedin.com/in/dharmendra-s-77297014/',
          imageObjectPosition: '50% 15%',
        },
        {
          name: 'Ramesh Behera',
          role: 'Co-Founder',
          bio: 'A core part of the GoGrabJob team, helping build tools that make a real difference in the lives of job seekers across India and beyond.',
          image: '/team/ramesh-behera.png',
          linkedIn: 'https://www.linkedin.com/in/ramesh-behera-97386616/',
          imageObjectPosition: '50% 15%',
        },
      ],
    },
    {
      title: '10. Company information',
      paragraphs: [
        'Legal entity: Zykes Technologies LLP',
        'Registered office: Bengaluru, Karnataka, India',
        'Product: GoGrabJob — AI job search platform',
        'Support: support@gograbjob.com',
        'Careers: we hire remotely across India — see our Careers page for open roles.',
      ],
    },
    {
      title: '11. Work with us',
      cta: {
        title: 'Ready to transform your job search?',
        body: 'Start free with 3 lifetime optimizations. Upgrade when you are applying seriously — cancel anytime.',
        primaryLabel: 'Get started free',
        primaryHref: '/signup',
        secondaryLabel: 'Read FAQs',
        secondaryHref: '/faqs',
      },
    },
  ],
};
